<script setup lang="ts">
import BaseInfo from './components/BaseInfo.vue'
import QuickMenu from './components/QuickMenu/index.vue'
import Today from './components/Today.vue'
import GroupToday from './components/Group/Today.vue'
import TradingMonth from './components/TradingMonth.vue'
import GroupTradingMonth from './components/Group/TradingMonth.vue'
import PayType from './components/PayType.vue'
import Top10 from './components/Group/Top10.vue'
import AccountOverview from './components/AccountOverview.vue'
import { GroupOfficeSelector } from '@mp/component'
import QR from './components/QR.vue'
import { useIsSp, usePermissions, useUserInfo } from '@mp/hooks'
import { computed, onMounted, provide, ref } from 'vue'

import { useTrackPagePropHelper } from '@sp/track'
import Notice from './components/Notice/index.vue'
import { useQuery } from '@tanstack/vue-query'
import { messageRecordLatestUnread } from '@mp/api/sdpMessage/MessageV2Controller.ts'
import { ArrowRight, WarningFilled } from '@element-plus/icons-vue'
import WujieVue from 'wujie-vue3'
import { isWindows, signObj } from '@mp/utils'

const { isNotSp, company } = useIsSp()
const ToDImage = `${import.meta.env.VITE_B_URL}/merchant-platform/agent-1.svg`

const { bus } = WujieVue

const { isHeadOffice, userInfo, isAdmin } = useUserInfo()
const officeCode = ref<string | undefined>(undefined)
const visibleTour = ref<boolean>(false)
const { memberRoles } = usePermissions()

// ?是否是代理商
const isAgent = computed(() => memberRoles.value?.includes('SDPPRO_AGGREGATION_AGENT'))

const { addPageProps } = useTrackPagePropHelper()
addPageProps({
  userType: isHeadOffice.value ? '总部商户' : (isAgent.value ? '代理商' : '商户'),
  userRole: isAdmin.value ? '管理员' : '操作员',
  merchantId: userInfo.value?.merchantId,
  operatorId: userInfo.value?.operatorId,
})

// 提供商户层级选择器的值给子组件使用
provide('officeCode', officeCode)

// #获取最近一条未读的消息
const { data: latestUnreadData, refetch: refetchLatestUnreadData } = useQuery({
  queryKey: ['message/record/latest-unread'],
  queryFn: () => messageRecordLatestUnread({
    targetAppId: 'WEB_B',
  }),
  select: res => res?.data,
})

bus.$on('notice:detail:close', () => {
  refetchLatestUnreadData()
})

onMounted(async () => {
  // ? 新手引导
  setTimeout(() => {
    if (!localStorage.getItem('tour_guide_finished'))
      visibleTour.value = true
  }, 1000)

  if (!isWindows())
    return

  // ? 检查是否安装，或者本地网络反问是否允许
  const getProviderRes = await signObj.getProviderAsync('coreCrypto', 0)
  if (!getProviderRes && localStorage.getItem('IS_READED_UNINSTALLED_SECURITY_COMPONENT') !== 'TRUE' && !isNotSp) {
    ElNotification({
      title: '提示',
      message: '当前页面加载较慢 <a href="/merchant-platform/盛意旺SaaS平台使用问题Q&A/index.html" class="text-primary" target="_blank">点击这里查看解决方法</a>',
      type: 'warning',
      duration: 0,
      dangerouslyUseHTMLString: true,
      onClose() {
        localStorage.setItem('IS_READED_UNINSTALLED_SECURITY_COMPONENT', 'TRUE')
      },
    })
  }
})

function handleTourFinish() {
  localStorage.setItem('tour_guide_finished', 'true')
}
</script>

<template>
  <section class="flex items-start gap-6">
    <section class="flex-1 overflow-hidden">
      <div
        v-if="latestUnreadData?.messageId && latestUnreadData.messageDsl?.display?.displayType === 'banner'"
        class="px-4 py-2 text-sm text-warn flex items-center rounded-md mb-4"
        style="border: 1px solid var(---Y50, rgba(236, 154, 59, 0.50));background: linear-gradient(0deg, rgba(236, 154, 59, 0.05) 0%, rgba(236, 154, 59, 0.05) 100%), #FFF;"
      >
        <el-icon :size="16" class="mr-1">
          <WarningFilled />
        </el-icon>
        {{ latestUnreadData?.content?.title }}
        <el-button class="ml-auto" link type="warning" @click="bus.$emit('notice:view', latestUnreadData?.messageId)">
          去查看 <el-icon>
            <ArrowRight />
          </el-icon>
        </el-button>
      </div>

      <!-- 常用功能 -->
      <QuickMenu />

      <div class="relative">
        <div v-if="isAgent" class="absolute text-base flex items-center justify-center flex-col text-center text-n-800 font-medium  top-0 left-0 bottom-0 right-0 bg-[rgba(255,255,255,0.6)] backdrop-blur-sm z-10">
          <img :src="ToDImage" alt="" class=" w-[340px] mx-auto block mb-8">
          <p>此功能已迁移至合作伙伴平台</p>
          <p>请移驾 <a href="https://d.shengpay.com" target="_blank" class="text-primary">d.shengpay.com</a></p>
        </div>

        <div v-if="isHeadOffice" class="flex items-center mt-6">
          <label class="block text-lg text-n-900 leading-none">交易统计</label>
          <div class="flex items-center text-sm text-n-400 ml-auto">
            <span class="mr-2"> 商户层级:</span>
            <GroupOfficeSelector id="DASHBOARD_OFFICE_SELECTOR" v-model="officeCode" v-track width="300px" />
          </div>
        </div>

        <!-- 总部商户 -->
        <template v-if="isHeadOffice">
          <GroupToday class="mt-4" />
          <GroupTradingMonth />
          <Top10 />
        </template>
        <template v-else>
          <Today class="mt-4" />

          <div class="grid 2xl:grid-cols-2 grid-cols-1 gap-4 mt-4 ">
            <!-- 近30日交易趋势 -->
            <div>
              <TradingMonth />
            </div>

            <!-- 今日渠道交易数据 & 商户top10 -->
            <div>
              <PayType />
            </div>
          </div>
        </template>
      </div>
    </section>

    <section class="w-[360px]">
      <BaseInfo />
      <AccountOverview v-if="!isNotSp" class="mt-4" />
      <Notice class="mt-4" />
      <QR class="mt-4" />
    </section>
  </section>

  <el-tour
    v-model="visibleTour" style="
      --el-tour-title-font-weight: 500;
      --el-tour-padding-primary: 20px;
      --el-tour-text-color: var(--n-400);
      --el-tour-border-radius: 8px;
    " @close="handleTourFinish"
  >
    <el-tour-step
      placement="bottom-start" target="#headerMenu" :title="isNotSp ? `欢迎使用${company}商户平台` : '欢迎使用盛意旺SaaS商户平台'"
      description="功能菜单更新为顶部导航，点击可查看对应二级菜单"
    />
    <el-tour-step target="#QUICKMENU" title="常用功能快捷入口" description="交易查询、账单查询、门店管理等常用功能可一键直达" />
    <el-tour-step target="#DASHBOARD_TODAY" title="今日实时数据看板" description="可快速了解您今日实时交易汇总数据、近30天交易趋势和交易渠道分布" />
    <el-tour-step
      v-if="!isNotSp"
      placement="left" target="#DASHBOARD_ACCOUNT_OVERVIEW" title="账户总览优化"
      description="可快速了解您的可用余额、冻结余额、待结算余额" :next-button-props="{
        children: '知道了，开始使用',
      }"
    />

    <template #indicators="{ current, total }">
      <span class="text-xs">{{ current + 1 }} / {{ total }}</span>
    </template>
  </el-tour>
</template>
